<template>
  <div class="role-page">
    <div class="back-row">
      <button type="button" class="back" @click="$emit('back')">
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path d="M19 12H6M12 6l-6 6 6 6" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        返回
      </button>
      <span class="crumb">{{ scopeName }} · {{ editionName }}</span>
    </div>

    <header class="page-head">
      <h1>选择评价角色</h1>
      <p>
        按「三方十类」定位本次评价对象。项目级以项目团队为评价主体，
        企业级以法人单位/集团为评价主体；同一指标在不同角色下对应不同的佐证材料。
      </p>
    </header>

    <section class="form-block">
      <label class="field">
        <span class="field-label">{{ subjectLabel }}</span>
        <el-input
          :model-value="subjectName"
          :placeholder="`请输入${subjectLabel}`"
          maxlength="60"
          show-word-limit
          class="field-input"
          @update:model-value="(v) => $emit('update:subjectName', v)"
        />
      </label>

      <div class="field">
        <span class="field-label">流程说明</span>
        <ul class="flow-list">
          <li><em>1</em>选择评价角色（下方三方十类）</li>
          <li><em>2</em>结合定位图与等级表做<strong>自评预判</strong>（可跳过，不参与打分）</li>
          <li><em>3</em>按六能力域逐项评分，系统加权定级</li>
        </ul>
      </div>
    </section>

    <section v-for="party in parties" :key="party.id" class="party">
      <div class="party-head">
        <h2>{{ party.name }}</h2>
        <p>{{ party.desc }}</p>
      </div>
      <div class="role-grid">
        <button
          v-for="role in rolesOf(party.id)"
          :key="role.id"
          type="button"
          class="role-card"
          :class="{ 'is-active': role.id === roleId }"
          @click="$emit('update:roleId', role.id)"
        >
          <span class="role-name">{{ role.name }}</span>
          <span class="role-posts">
            <template v-if="isProject">
              <em>项目级岗位</em>
              {{ role.projectPosts.join('、') }}
            </template>
            <template v-else>
              <em>佐证特征</em>
              制度 / 架构 / 清单类材料
            </template>
          </span>
          <span class="role-check" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="14" height="14">
              <path d="M5 13l4 4L19 7" fill="none" stroke="currentColor"
                    stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
        </button>
      </div>
    </section>

    <footer class="page-foot">
      <el-button type="primary" round size="large" :disabled="!canNext" @click="$emit('next')">
        下一步：自评预判
      </el-button>
      <p v-if="!canNext" class="foot-tip">请填写{{ subjectLabel }}并选择评价角色</p>
    </footer>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { PARTIES, ROLES, ROLES_BY_PARTY, SCOPES, EDITIONS } from '../data/index.js'

const props = defineProps({
  scope: { type: String, default: 'project' },
  edition: { type: String, default: 'online' },
  roleId: { type: String, default: '' },
  subjectName: { type: String, default: '' }
})

defineEmits(['back', 'next', 'update:roleId', 'update:subjectName'])

const parties = PARTIES

const isProject = computed(() => props.scope === 'project')
const scopeName = computed(() => (SCOPES[props.scope] || {}).name || '')
const editionName = computed(() => (EDITIONS[props.edition] || {}).name || '')
const subjectLabel = computed(() => (SCOPES[props.scope] || {}).subjectLabel || '名称')

const canNext = computed(
  () => props.subjectName.trim().length > 0 && !!props.roleId
)

function rolesOf (partyId) {
  return ROLES_BY_PARTY[partyId] || ROLES.filter((r) => r.party === partyId)
}
</script>

<style lang="scss" scoped>
.role-page {
  max-width: 1180px;
  margin: 0 auto;
  padding: 28px 24px 64px;
  text-align: left;
}

.back-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px 7px 12px;
  font-size: 13px;
  color: #06c;
  background: rgba(0, 113, 227, 0.08);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.25s ease;

  &:hover {
    background: rgba(0, 113, 227, 0.16);
  }
}

.crumb {
  font-size: 13px;
  color: #86868b;
}

.page-head {
  margin-top: 26px;

  h1 {
    margin: 0;
    font-size: clamp(26px, 3.6vw, 40px);
    font-weight: 600;
    letter-spacing: 0.03em;
    color: #1d1d1f;
  }

  p {
    margin: 12px 0 0;
    max-width: 760px;
    font-size: 14px;
    line-height: 1.9;
    color: #6e6e73;
  }
}

.form-block {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  margin-top: 32px;
  padding: 26px 28px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
}

.field {
  display: block;
}

.field-label {
  display: block;
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #3a3a3c;
}

.field-hint {
  font-style: normal;
  font-weight: 400;
  color: #86868b;
}

.field-input {
  max-width: 420px;
}

.flow-list {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 7px 0;
    font-size: 13px;
    line-height: 1.7;
    color: #6e6e73;

    strong {
      color: #0071e3;
      font-weight: 600;
    }
  }

  em {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    font-style: normal;
    font-size: 11.5px;
    font-weight: 600;
    color: #ffffff;
    background: #0071e3;
    border-radius: 50%;
  }
}

.party {
  margin-top: 40px;
}

.party-head {
  display: flex;
  align-items: baseline;
  gap: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  h2 {
    margin: 0;
    font-size: 19px;
    font-weight: 600;
    color: #1d1d1f;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: #86868b;
  }
}

.role-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
  margin-top: 18px;
}

.role-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 46px 18px 20px;
  text-align: left;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  cursor: pointer;
  transition: border-color 0.24s ease, box-shadow 0.24s ease, transform 0.24s ease;

  &:hover {
    border-color: rgba(0, 113, 227, 0.4);
    box-shadow: 0 8px 22px rgba(0, 113, 227, 0.10);
    transform: translateY(-2px);
  }

  &.is-active {
    border-color: #0071e3;
    box-shadow: 0 0 0 3px rgba(0, 113, 227, 0.14);
  }
}

.role-name {
  font-size: 15.5px;
  font-weight: 600;
  color: #1d1d1f;
}

.role-posts {
  font-size: 12.5px;
  line-height: 1.7;
  color: #6e6e73;

  em {
    display: inline-block;
    margin-right: 8px;
    padding: 1px 8px;
    font-style: normal;
    font-size: 11.5px;
    color: #0071e3;
    background: rgba(0, 113, 227, 0.09);
    border-radius: 4px;
  }
}

.role-check {
  position: absolute;
  top: 18px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  color: #ffffff;
  background: #0071e3;
  border-radius: 50%;
  opacity: 0;
  transform: scale(0.6);
  transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);

  .role-card.is-active & {
    opacity: 1;
    transform: none;
  }
}

.page-foot {
  margin-top: 44px;
  text-align: center;
}

.foot-tip {
  margin: 12px 0 0;
  font-size: 12.5px;
  color: #d1681f;
}

@media (max-width: 860px) {
  .form-block {
    grid-template-columns: minmax(0, 1fr);
  }

  .party-head {
    flex-direction: column;
    gap: 4px;
  }
}
</style>
